import { useCallback, useMemo } from "react";
import { AppId, FCX, Tabs, usePlatformContext } from "@datassential/platform-ui-lib";
import { useParams } from "react-router-dom";
import useCategoryProfileMenu, { CategoryProfileMenuChart } from "@apiHooks/useCategoryProfileMenu";
import useCategoryProfilePopularityTrendGap from "@apiHooks/useCategoryProfilePopularityTrendGap"
import useLocalStorageState from "@hooks/useLocalStorageState";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { HomeRankingTabEnum } from "@enums";
import AccessLink from "@components/AccessLink/AccessLink";
import MenuPenetrationChart from "./components/MenuPenetrationChart/MenuPenetrationChart";
import RegionalMenuSkews from "./components/RegionalMenuSkews/RegionalMenuSkews";
import CategoryProfileSectionTitle from "@pages/CategoryProfile/components/CategoryProfileSectionTitle/CategoryProfileSectionTitle";
import CategoryProfileSubtitle from "@pages/CategoryProfile/components/CategoryProfileSubtitle/CategoryProfileSubtitle";
import CategoryProfileSelect from "@pages/CategoryProfile/components/CategoryProfileSelect/CategoryProfileSelect";
import CategoryProfileCard from "@pages/CategoryProfile/components/CategoryProfileCard/CategoryProfileCard";
import { MENU_GAPS_STORAGE_KEY, MENU_RANKING_STORAGE_KEY, MENU_RANKING_TABS, MENU_SELECTED_IC_STORAGE_KEY, MenuGapsEnum, MENU_GAPS_TABS } from "./Menu.constants";
import PopularityTrendGapChart from "@pages/CategoryProfile/components/PopularityTrendGapChart/PopularityTrendGapChart";
import { buildMenuTrendsByoUrl, formatMenuChartTitle } from "./Menu.helpers";
import { CategoryProfileSectionComponentProps } from "@pages/CategoryProfile/CategoryProfile.constants";
import "./Menu.styles.scss";

export const useMenuSectionData = (categoryId: string) => {
    const { data, isLoading } = useCategoryProfileMenu(categoryId);
    return { hasData: data.length > 0, isLoading, data };
};

const CategoryProfileMenu: FCX<CategoryProfileSectionComponentProps> = ({ title, categoryName, data }) => {
    const { id: categoryId } = useParams<{ id: string }>();
    const charts = (data as CategoryProfileMenuChart[] | undefined) ?? [];

    const { data: gapData } = useCategoryProfilePopularityTrendGap(categoryId);


    const [storedChartId, setStoredChartId] = useLocalStorageState<string>(
        MENU_SELECTED_IC_STORAGE_KEY(categoryId),
        '',
    );
    const [rankingTab, setRankingTab] = useLocalStorageState<HomeRankingTabEnum>(
        MENU_RANKING_STORAGE_KEY,
        HomeRankingTabEnum.WHATS_TOP,
    );

    const [gapTab, setGapTab] = useLocalStorageState<MenuGapsEnum>(
        MENU_GAPS_STORAGE_KEY,
        MenuGapsEnum.POPULARITY,
    );

    const selectedChart = useMemo(
        () => charts.find(chart => String(chart.instantChartId) === storedChartId) ?? charts[0],
        [charts, storedChartId],
    );
    const selectedChartId = selectedChart?.instantChartId;

    // Find the gap entry matching the currently selected instant chart.
    // The scatter plot is hidden when no matching entry exists or it has no keywords.
    const selectedGapEntry = useMemo(
        () => gapData.find(entry => entry.instantChartId === selectedChartId),
        [gapData, selectedChartId],
    );
    const gapKeywords = selectedGapEntry?.keywords ?? [];
    const hasGapKeywords = gapKeywords.length > 0;

    const { userInfo: { products } } = usePlatformContext();
    const menuTrends = useMemo(
        () => products[AppId.MenuTrends],
        [products],
    );

    const byoUrl = useMemo(
        () => buildMenuTrendsByoUrl(menuTrends?.url, gapKeywords),
        [menuTrends, gapKeywords],
    );

    const chartOptions = useMemo(
        () => charts.map(chart => ({
            id: chart.instantChartId,
            title: formatMenuChartTitle(chart),
        })),
        [charts],
    );

    const isGrowth = rankingTab === HomeRankingTabEnum.WHATS_GROWING;

    const chartData = isGrowth
        ? selectedChart?.topByGrowth ?? []
        : selectedChart?.topByPenetration ?? [];

    const handleSelectChart = useCallback(
        (id: number) => setStoredChartId(String(id)),
        [setStoredChartId],
    );

    const handleDownload = useCallback(
        () => http.downloadFileXHRFromUrl(
            'GET',
            `${apiConfig['get category profile menu ppt']}?categoryProfileId=${categoryId}&instantChartId=${selectedChartId}`,
        ),
        [selectedChartId],
    );

    const hasCharts = charts.length > 0;

    if (!hasCharts) return null;

    return (
        <div className="CategoryProfile__section-content CategoryProfileMenu">
            <CategoryProfileSectionTitle
                title={title}
                onDownload={hasCharts ? handleDownload : undefined}
            />

            {hasCharts && (
                <CategoryProfileCard>
                    <div className="CategoryProfileMenu__header">
                        <div className="CategoryProfileMenu__header-left">
                            <CategoryProfileSubtitle>
                                {`${isGrowth ? 'past 4-year growth' : 'menu penetration'}: ${categoryName}`}
                            </CategoryProfileSubtitle>
                            <CategoryProfileSelect
                                options={chartOptions}
                                value={selectedChartId}
                                setValue={handleSelectChart}
                            />
                        </div>
                        <Tabs
                            data={MENU_RANKING_TABS}
                            activeTabId={rankingTab}
                            onItemClick={setRankingTab}
                        />
                    </div>

                    <MenuPenetrationChart
                        data={chartData}
                        rankingTab={rankingTab}
                    />

                    <RegionalMenuSkews
                        skews={selectedChart?.regionalSkews ?? []}
                        categoryName={categoryName}
                    />

                    {hasGapKeywords && (
                        <>
                            <div className="CategoryProfile__separator" />

                            <div className="CategoryProfileMenu__header">
                                <div className="CategoryProfileMenu__header-left">
                                    {/* View title switches with the active tab. */}
                                    <CategoryProfileSubtitle>
                                        {`${gapTab === MenuGapsEnum.POPULARITY ? 'popularity gap' : 'trend gap'}: ${categoryName}`}
                                    </CategoryProfileSubtitle>
                                    <span className="CategoryProfileMenu__gap-chart-name">
                                        {formatMenuChartTitle(selectedChart).toUpperCase()}
                                    </span>
                                </div>
                                <Tabs
                                    className="CategoryProfileMenu__gap-tabs"
                                    data={MENU_GAPS_TABS}
                                    activeTabId={gapTab}
                                    onItemClick={setGapTab}
                                />
                            </div>

                            <PopularityTrendGapChart
                                keywords={gapKeywords}
                                activeView={gapTab}
                            />
                        </>
                    )}
                </CategoryProfileCard>
            )}

            <div className="CategoryProfileMenu__more-link">
                <AccessLink
                    url={byoUrl}
                    text="More Menu Trends"
                    testId="linkCategoryProfileMenuMoreMenuTrends"
                />
            </div>
        </div>
    );
};

export default CategoryProfileMenu;
